import type { DateRange, DurationOption } from '@/types/SearchBar.ts';
import { daysBetween } from '@/utils/date/saturdayWindow';

const DAYS_PER_WEEK = 7;

/** Whether the user asked for their exact dates or for a length within them. */
export type DurationMode = 'exact' | 'preset';

/**
 * Absence of an explicit signal means `'preset'`.
 *
 * "Exact" is a statement of intent, so it only ever comes from the user
 * clicking it: every link without `?dmode`, every stale storage blob and every
 * fresh page load starts out flexible. Defaulting the other way would switch
 * the feature off in the most common search there is — the one-week default.
 */
export const DEFAULT_DURATION_MODE: DurationMode = 'preset';

/** Narrow an untrusted URL / storage value, returning `undefined` for anything else. */
export function parseDurationMode(raw: unknown): DurationMode | undefined {
  return raw === 'exact' || raw === 'preset' ? raw : undefined;
}

/**
 * Resolve the persisted duration-mode intent: **URL > sessionStorage > preset**.
 *
 * Deliberately not `resolveField`'s order (`ss > url`): a freshly pasted
 * `?dmode=exact` has to beat a sessionStorage entry left over from back/forward
 * navigation. It is also never gated on `showSearchBar`, because the mode
 * changes which offers come back, not just what the UI shows.
 */
export function resolveDurationIntent(
  fromUrl?: DurationMode,
  fromSs?: DurationMode | null
): DurationMode {
  return fromUrl ?? fromSs ?? DEFAULT_DURATION_MODE;
}

/**
 * Number of whole nights spanned by a date range, using the shared
 * {@link daysBetween} (local-midnight, DST-safe) so it agrees exactly with the
 * Saturday-window widening and period-consistency checks. Returns `0` for an
 * incomplete range.
 */
export function nightsInRange(range: DateRange): number {
  if (!range || !(range[0] instanceof Date) || !(range[1] instanceof Date)) {
    return 0;
  }
  return Math.abs(daysBetween(range[0], range[1]));
}

/**
 * Largest configured duration preset that is a whole number of weeks (a multiple
 * of 7). Used as the cap for {@link roundUpToWeekly} so zero-result auto-extend
 * never grows a stay beyond the longest weekly preset the site offers (today
 * 21 = 3 weeks). Returns `0` when no weekly preset exists.
 */
export function maxWeeklyPreset(options: DurationOption[]): number {
  return options.reduce(
    (max, o) =>
      o.type === 'preset' && o.value % DAYS_PER_WEEK === 0 && o.value > max ? o.value : max,
    0
  );
}

/**
 * Round a trip `duration` up to the nearest whole week (multiple of 7) for the
 * zero-result auto-extend, matching Saturday-to-Saturday hotels that only sell
 * weekly stays.
 *
 * The result is capped at `maxWeekly` (the longest weekly preset): a duration
 * already longer than that cap is left untouched — we never silently shorten an
 * explicitly requested long stay. A duration that is already a whole number of
 * weeks (and within the cap) is returned unchanged. `maxWeekly <= 0` (no weekly
 * preset configured) also leaves the duration untouched.
 */
export function roundUpToWeekly(duration: number, maxWeekly: number): number {
  if (duration <= 0 || maxWeekly <= 0 || duration > maxWeekly) {
    return duration;
  }
  return Math.ceil(duration / DAYS_PER_WEEK) * DAYS_PER_WEEK;
}

/**
 * Whether `duration` is one of the configured presets, i.e. has a chip of its
 * own in the duration selector. Exported because a duration that is *not* a
 * preset is the ad-hoc case the modal has to render an extra chip for; that
 * check must not be inferred from {@link resolveDurationMode}.
 */
export function isPreset(duration: number, options: DurationOption[]): boolean {
  return options.some((o) => o.type === 'preset' && o.value === duration);
}
